import React, { useState, useMemo } from 'react';
import './Revistas.css';
import { apiFetch } from './api';
import { useRecurso } from './hooks/useRecurso';
import EstadoCarregando from './EstadoCarregando';
import EstadoVazio from './EstadoVazio';
import EstadoErro from './EstadoErro';

// Datas do backend chegam ISO (LocalDate "YYYY-MM-DD"); o produto exibe
// DD/MM/YYYY (convenção CLAUDE.md). Tratado como data local — sem fuso.
function formatarData(iso) {
  if (!iso) return '—';
  const partes = String(iso).slice(0, 10).split('-');
  if (partes.length !== 3) return iso;
  const [ano, mes, dia] = partes;
  return `${dia}/${mes}/${ano}`;
}

// Mapeia o item do backend (RevistaListItemResponse) para o shape de exibição.
function mapRevista(r) {
  return { ...r, data: formatarData(r.data) };
}

// Mensagens pt-BR por código de erro do backend (contrato { error: { code } }).
const MENSAGENS_ERRO = {
  FORBIDDEN: 'Você não tem permissão para ver as revistas.',
  RATE_LIMITED: 'Muitas requisições. Aguarde um momento e tente novamente.',
};

function mensagemErro(erro) {
  if (!erro) return '';
  if (erro.status === 429) return MENSAGENS_ERRO.RATE_LIMITED;
  if (erro.code && MENSAGENS_ERRO[erro.code]) return MENSAGENS_ERRO[erro.code];
  if (erro.name === 'ApiError') return 'Não foi possível carregar as revistas da RPI.';
  return 'Erro de conexão. Verifique sua internet e tente novamente.';
}

function Revistas() {
  // Modal de visualização
  const [modalAberto, setModalAberto] = useState(false);
  const [revistaSelecionada, setRevistaSelecionada] = useState(null);
  // Detalhe da revista (agregados de ações do tenant) — GET /api/revistas/{numero}
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [erroDetalhe, setErroDetalhe] = useState(false);
  // Download autenticado do PDF da revista
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [erroPdf, setErroPdf] = useState(null);
  // Filtros (aplicados client-side sobre a página carregada)
  const [filtros, setFiltros] = useState({
    status: 'todos',
    notificacao: 'todos',
    dataInicio: '',
    dataFim: ''
  });

  // Lista de revistas vinda do backend real (PRD 14 / SEF-84). Revistas são
  // globais (não filtram por tenant). Os filtros abaixo são aplicados
  // client-side sobre a página carregada; filtro + paginação server-side é
  // follow-up.
  const { dados, carregando, erro, recarregar } = useRecurso(
    () => apiFetch('/api/revistas?page=1&perPage=20'),
    []
  );
  const revistas = useMemo(() => (dados?.data || []).map(mapRevista), [dados]);

  // Abre o modal e busca os agregados reais de ações do tenant para a revista.
  const abrirModal = async (revista) => {
    setRevistaSelecionada(revista);
    setModalAberto(true);
    setDetalhe(null);
    setErroDetalhe(false);
    setErroPdf(null);
    setCarregandoDetalhe(true);
    try {
      const d = await apiFetch(`/api/revistas/${revista.numero}`);
      setDetalhe(d);
    } catch (e) {
      setErroDetalhe(true);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const fecharModal = () => {
    setModalAberto(false);
    setRevistaSelecionada(null);
    setDetalhe(null);
    setErroDetalhe(false);
    setErroPdf(null);
  };

  // Download autenticado do PDF (o endpoint exige Bearer; um <a href> simples
  // tomaria 401). Busca o blob via apiFetch e dispara o download no navegador.
  const baixarPdf = async (numero) => {
    setBaixandoPdf(true);
    setErroPdf(null);
    try {
      const blob = await apiFetch(`/api/revistas/${numero}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `revista-${numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErroPdf(
        e?.status === 404
          ? 'PDF indisponível para esta revista.'
          : 'Não foi possível baixar o PDF. Tente novamente.'
      );
    } finally {
      setBaixandoPdf(false);
    }
  };

  // Filtra a página carregada conforme os filtros selecionados.
  const revistasFiltradas = () => {
    return revistas.filter(revista => {
      if (filtros.status !== 'todos' && revista.status !== filtros.status) {
        return false;
      }

      if (filtros.notificacao !== 'todos' && revista.notificacaoStatus !== filtros.notificacao) {
        return false;
      }

      if (filtros.dataInicio && new Date(revista.data.split('/').reverse().join('-')) < new Date(filtros.dataInicio)) {
        return false;
      }

      if (filtros.dataFim && new Date(revista.data.split('/').reverse().join('-')) > new Date(filtros.dataFim)) {
        return false;
      }

      return true;
    });
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros({
      ...filtros,
      [name]: value
    });
  };

  const limparFiltros = () => {
    setFiltros({
      status: 'todos',
      notificacao: 'todos',
      dataInicio: '',
      dataFim: ''
    });
  };

  // Classe CSS conforme o status de notificação.
  const getNotificacaoClasse = (status) => {
    switch (status) {
      case 'Enviada':
        return 'notificacao-enviada';
      case 'Não enviada':
        return 'notificacao-nao-enviada';
      case 'Pendente':
        return 'notificacao-pendente';
      default:
        return '';
    }
  };

  // Classe CSS conforme o progresso do crawler.
  const getCrawlerClasse = (percentual) => {
    if (percentual === 100) {
      return 'crawler-concluido';
    } else if (percentual >= 70) {
      return 'crawler-avancado';
    } else if (percentual >= 30) {
      return 'crawler-intermediario';
    } else {
      return 'crawler-inicial';
    }
  };

  const statusClasse = (status) => `status ${(status || '').toLowerCase().replace(' ', '-')}`;

  const totalNaoNotificadas = revistas.filter(r => r.notificacaoStatus === "Não enviada").length;
  const totalEmProcessamento = revistas.filter(r => r.statusCrawler < 100).length;

  return (
    <div className="revistas-container">
      <div className="revistas-header">
        <h1>Revistas do INPI</h1>
        <p className="descricao">
          Monitoramento e análise das publicações oficiais de propriedade industrial
        </p>
      </div>

      {/* Resumo de Processamento */}
      <div className="estatisticas-container">
        <h3>Resumo de Processamento</h3>
        <div className="estatisticas-grid">
          <div className="estatistica-item">
            <h4>Total de Revistas</h4>
            <p className="valor">{revistas.length}</p>
          </div>
          <div className="estatistica-item">
            <h4>Processadas</h4>
            <p className="valor">{revistas.filter(r => r.status === "Processada").length}</p>
          </div>
          <div className="estatistica-item">
            <h4>Em Processamento</h4>
            <p className="valor">{revistas.filter(r => r.status === "Em processamento").length}</p>
          </div>
          <div className={`estatistica-item ${totalNaoNotificadas > 0 ? 'tem-pendencia' : ''}`}>
            <h4>Notificações Pendentes</h4>
            <p className="valor notificacao-nao-enviada">{totalNaoNotificadas}</p>
          </div>
          <div className={`estatistica-item ${totalEmProcessamento > 0 ? 'tem-pendencia' : ''}`}>
            <h4>Crawlers Ativos</h4>
            <p className="valor crawler-andamento">{totalEmProcessamento}</p>
          </div>
        </div>
      </div>

      <div className="filtros-container">
        <div className="filtros-principais">
          <div className="filtro-grupo">
            <label htmlFor="status">Status:</label>
            <select
              id="status"
              name="status"
              value={filtros.status}
              onChange={handleFiltroChange}
            >
              <option value="todos">Todos</option>
              <option value="Processada">Processadas</option>
              <option value="Em processamento">Em processamento</option>
              <option value="Pendente">Pendentes</option>
              <option value="Erro">Com erro</option>
            </select>
          </div>

          <div className="filtro-grupo">
            <label htmlFor="notificacao">Notificações:</label>
            <select
              id="notificacao"
              name="notificacao"
              value={filtros.notificacao}
              onChange={handleFiltroChange}
            >
              <option value="todos">Todas</option>
              <option value="Enviada">Enviadas</option>
              <option value="Não enviada">Não enviadas</option>
              <option value="Pendente">Pendentes</option>
            </select>
          </div>
        </div>

        <div className="filtros-data">
          <div className="data-row">
            <div className="filtro-grupo data-grupo">
              <label htmlFor="dataInicio">De:</label>
              <input
                type="date"
                id="dataInicio"
                name="dataInicio"
                value={filtros.dataInicio}
                onChange={handleFiltroChange}
              />
            </div>

            <div className="filtro-grupo data-grupo">
              <label htmlFor="dataFim">Até:</label>
              <input
                type="date"
                id="dataFim"
                name="dataFim"
                value={filtros.dataFim}
                onChange={handleFiltroChange}
              />
            </div>
          </div>

          <button
            className="limpar-filtros"
            onClick={limparFiltros}
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {carregando ? (
        <EstadoCarregando mensagem="Carregando revistas..." />
      ) : erro ? (
        <EstadoErro mensagem={mensagemErro(erro)} onRetry={recarregar} />
      ) : revistasFiltradas().length === 0 ? (
        <EstadoVazio
          icone="fa-solid fa-book"
          titulo="Nenhuma revista encontrada"
          descricao="Não há revistas da RPI para os filtros selecionados."
        />
      ) : (
        <div className="tabela-container">
          <table className="tabela-revistas">
            <thead>
              <tr>
                <th>Revista Nº</th>
                <th>Data</th>
                <th>Status</th>
                <th>Notificações</th>
                <th>Crawler</th>
                <th>Processos</th>
                <th>Despachos</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {revistasFiltradas().map(revista => (
                <tr key={revista.id}>
                  <td><strong>{revista.numero}</strong></td>
                  <td>{revista.data}</td>
                  <td>
                    <span className={statusClasse(revista.status)}>
                      {revista.status}
                    </span>
                  </td>
                  <td>
                    <span className={`notificacao-status ${getNotificacaoClasse(revista.notificacaoStatus)}`}>
                      {revista.notificacaoStatus}
                    </span>
                  </td>
                  <td>
                    <div className="crawler-progresso-container">
                      <div className={`crawler-progresso ${getCrawlerClasse(revista.statusCrawler)}`} style={{width: `${revista.statusCrawler}%`}}></div>
                      <span className="crawler-percentual">{revista.statusCrawler}%</span>
                    </div>
                  </td>
                  <td>{revista.processos}</td>
                  <td>{revista.despachos || '-'}</td>
                  <td>
                    <div className="acoes-revista">
                      <button
                        className="botao-acao visualizar"
                        onClick={() => abrirModal(revista)}
                      >
                        Visualizar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Visualização */}
      {modalAberto && revistaSelecionada && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-conteudo" onClick={e => e.stopPropagation()}>
            <div className="modal-cabecalho">
              <h2>Revista {revistaSelecionada.numero}</h2>
              <button className="modal-fechar" onClick={fecharModal}>×</button>
            </div>

            <div className="modal-corpo">
              <div className="modal-info-principal">
                <div className="modal-coluna">
                  <h3>Informações Gerais</h3>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Data:</span>
                    <span className="modal-info-valor">{revistaSelecionada.data}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Status:</span>
                    <span className={statusClasse(revistaSelecionada.status)}>
                      {revistaSelecionada.status}
                    </span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Status de Notificação:</span>
                    <span className={`notificacao-status ${getNotificacaoClasse(revistaSelecionada.notificacaoStatus)}`}>
                      {revistaSelecionada.notificacaoStatus}
                    </span>
                  </div>
                </div>
                <div className="modal-coluna">
                  <h3>Dados de Processamento</h3>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Total de Processos:</span>
                    <span className="modal-info-valor">{revistaSelecionada.processos}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Despachos:</span>
                    <span className="modal-info-valor">{revistaSelecionada.despachos || '-'}</span>
                  </div>
                  <div className="modal-info-item">
                    <span className="modal-info-label">Crawler:</span>
                    <div className="modal-crawler-container">
                      <div className="crawler-progresso-container modal-crawler">
                        <div
                          className={`crawler-progresso ${getCrawlerClasse(revistaSelecionada.statusCrawler)}`}
                          style={{width: `${revistaSelecionada.statusCrawler}%`}}
                        ></div>
                        <span className="crawler-percentual">{revistaSelecionada.statusCrawler}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo de Ações — agregados reais do tenant (SEF-84). A execução
                  das ações fica nos cards próprios (SEF-37/38/39). */}
              <div className="modal-secao acoes-necessarias">
                <h3>Resumo de Ações</h3>
                {carregandoDetalhe ? (
                  <EstadoCarregando mensagem="Carregando resumo de ações..." inline />
                ) : erroDetalhe || !detalhe ? (
                  <p className="modal-instrucao">Sem dados de ação disponíveis.</p>
                ) : (
                  <div className="modal-info-principal">
                    <div className="modal-coluna">
                      <div className="modal-info-item">
                        <span className="modal-info-label">Marcas afetadas:</span>
                        <span className="modal-info-valor">{detalhe.totalMarcasAfetadas}</span>
                      </div>
                      <div className="modal-info-item">
                        <span className="modal-info-label">Clientes afetados:</span>
                        <span className="modal-info-valor">{detalhe.totalClientesAfetados}</span>
                      </div>
                    </div>
                    <div className="modal-coluna">
                      <div className="modal-info-item">
                        <span className="modal-info-label">Oposições:</span>
                        <span className="modal-info-valor">{detalhe.oposicoes}</span>
                      </div>
                      <div className="modal-info-item">
                        <span className="modal-info-label">Recursos:</span>
                        <span className="modal-info-valor">{detalhe.recursos}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-rodape">
              <button
                className="botao-acao download"
                onClick={() => baixarPdf(revistaSelecionada.numero)}
                disabled={baixandoPdf}
              >
                {baixandoPdf ? 'Baixando...' : 'PDF'}
              </button>
              {erroPdf && <span className="pdf-erro" role="alert">{erroPdf}</span>}
              <button className="botao-acao fechar" onClick={fecharModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Revistas;
